import type { RestCreateGiftCard, RestGiftCard, RestUpdateGiftCard } from "@KassaroAB/shared/types/giftCard";
import type { GiftCardStatus } from "@KassaroAB/shared/types/enums/GiftCardStatus";
import type { GiftCardId, StoreId } from "@KassaroAB/shared/types/ids";
import type { Money } from "@KassaroAB/shared/types/money";
import { auth } from "../config/firebase";
import { API_BASE_URL } from "../constants/api";
import { api } from "../utils/api";
import {
  catalogCollectionPath,
  type CatalogScope,
} from "./catalogScope";

const giftCardsPath = (scope: CatalogScope) =>
  catalogCollectionPath(scope, "giftCards");

export const getGiftcardsApi = async (
  storeId: StoreId,
): Promise<RestGiftCard[]> => {
  return api.get(`stores/${storeId}/giftcards`);
};

export const getGiftcardsPaginatedApi = async (
  scope: CatalogScope,
  cursor: GiftCardId | null,
  limit: number = 20,
): Promise<RestGiftCard[]> => {
  const params = new URLSearchParams();
  if (cursor) {
    params.set("cursor", cursor);
  }
  params.set("limit", limit.toString());
  return api.get(`${giftCardsPath(scope)}?${params.toString()}`);
};

export const getGiftcardApi = async (
  storeId: StoreId,
  giftcardId: GiftCardId,
): Promise<RestGiftCard> => {
  return api.get(`stores/${storeId}/giftcards/${giftcardId}`);
};

export const createGiftcardApi = async (
  storeId: StoreId,
  giftCard: RestCreateGiftCard,
): Promise<RestGiftCard> => {
  return api.post(`stores/${storeId}/giftcards`, giftCard);
};

export const updateGiftcardApi = async (
  storeId: StoreId,
  giftcardId: GiftCardId,
  giftcard: RestUpdateGiftCard,
): Promise<RestGiftCard> => {
  return api.put(`stores/${storeId}/giftcards/${giftcardId}`, giftcard);
};

export const deleteGiftcardApi = async (
  storeId: StoreId,
  giftcardId: GiftCardId,
): Promise<void> => {
  return api.delete(`stores/${storeId}/giftcards/${giftcardId}`);
};

export interface ImportGiftcardsParams {
  columnMapping: string[];
  csvFile: File;
  defaultValues?: Record<string, string>;
  skipFirstRow: boolean;
  encoding: string;
  delimiter: string;
  dryrun: boolean;
  onlyUpdate: boolean;
}

export interface ImportGiftcardError {
  row: number;
  field?: string;
  message: string;
  value?: unknown;
}

export interface ImportValidGiftCard {
  id?: GiftCardId;
  cardNumber?: string;
  initialAmount?: Money;
  remainingAmount?: Money;
  validUntil?: string;
  status?: GiftCardStatus;
}

export interface ImportGiftcardsDryRunResults {
  success: boolean;
  totalRows: number;
  validRows: number;
  errorCount: number;
  errors?: ImportGiftcardError[];
  validGiftCards?: ImportValidGiftCard[];
}

export interface ImportGiftcardsResult {
  created: number;
  updated: number;
  skipped: number;
  failed: number;
}

export type ImportGiftcardsResponse = ImportGiftcardsDryRunResults | ImportGiftcardsResult;

export const importGiftcardsApi = async (
  storeId: StoreId,
  params: ImportGiftcardsParams,
): Promise<ImportGiftcardsResponse | null> => {
  if (!auth.currentUser) {
    throw new Error("User not authenticated");
  }

  return auth.currentUser
    .getIdToken()
    .then((token) => {
      const formData = new FormData();
      formData.append("columnMapping", JSON.stringify(params.columnMapping));
      formData.append("csvFile", params.csvFile);
      formData.append(
        "defaultValues",
        JSON.stringify(params.defaultValues ?? {}),
      );
      formData.append("skipFirstRow", params.skipFirstRow ? "true" : "false");
      formData.append("encoding", params.encoding);
      formData.append("delimiter", params.delimiter);
      formData.append("dryrun", params.dryrun ? "true" : "false");
      formData.append("onlyUpdate", params.onlyUpdate ? "true" : "false");

      const headers = new Headers();
      if (token) {
        headers.append("Authorization", `Bearer ${token}`);
      }

      return fetch(`${API_BASE_URL}/stores/${storeId}/giftcards/import`, {
        method: "POST",
        headers,
        body: formData,
      });
    })
    .then((response) => {
      if (!response.ok) {
        return response
          .json()
          .then((data: { message?: string }) => {
            throw new Error(data.message || `HTTP error ${response.status}`);
          })
          .catch((err: Error) => {
            throw new Error(err.message || `HTTP error ${response.status}`);
          });
      }
      if (response.status === 204) {
        return null;
      }
      return response.json();
    });
};
